'use client';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@ui/button';
import { ResponsiveImage } from '@web/molecules';
import { NewToOllie } from '@web/components';
import { Input, SmolLink, Heading } from '@web/atoms';
import { useReporter } from '../../reporter';
import {
  LoginContainer,
  LoginFormContainer,
  Form,
  ButtonContainer,
  HeadingContainer,
  SubHeading,
  InputContainer,
  ShopifyCallout,
  CanineCareCallout,
} from './styles.js';
import { onLoginView } from './events';
import { emailSchema } from './schema';
import SecuredByShopify from './secured-by-shopify.svg';

export const DualLoginForm = ({
  heading,
  labelEmail,
  labelPassword,
  onSubmit,
  handleRequestCustomerLogin,
  placeholderEmail,
  placeholderPassword,
  labelLogin,
  labelContinue,
  forgotPasswordUrl,
  forgotPasswordLabel,
  newToOllie,
  customerType,
  defaultValues,
  isCheckingEmail,
  canineCareUrl,
}) => {
  const isCustomerNonexistent = customerType === 'not found';

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues,
    resolver: yupResolver(emailSchema),
    mode: 'onTouched',
  });

  const isFormSubmitting = isSubmitting || isCheckingEmail;
  const reporter = useReporter();
  useEffect(() => {
    reporter.tag(onLoginView());
  }, [reporter]);

  return (
    <LoginContainer>
      <LoginFormContainer>
        <HeadingContainer>
          <Heading
            headingText={heading}
            headingLevel="h2"
            typography="heading2"
            position="center"
            backgroundColor="background.lightest"
          />
          <SubHeading>Enter your email to log into your account.</SubHeading>
        </HeadingContainer>
        <Form
          method="post"
          onSubmit={handleSubmit(async data => {
            // if customerType or customer doesn't exist, we
            // will submit the email to check login type
            // otherwise, if the customer is Legacy, we will
            // use the Legacy/BC login method
            if (!customerType || isCustomerNonexistent) {
              await handleRequestCustomerLogin(data?.email.toLowerCase());
            }
          })}
        >
          <InputContainer>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  labelText={labelEmail}
                  status={
                    fieldState.error || isCustomerNonexistent ? 'error' : null
                  }
                  error={fieldState.error?.message || ''}
                  placeholder={placeholderEmail || ''}
                  disabled={isCheckingEmail}
                />
              )}
            />
          </InputContainer>
          <ButtonContainer>
            <Button
              type="submit"
              fullWidth
              disabled={isFormSubmitting}
              isLoading={isFormSubmitting}
            >
              {!customerType ? labelContinue : labelLogin}
            </Button>
            <ShopifyCallout>
              <ResponsiveImage
                src={SecuredByShopify.src}
                alt="Secured by Shopify"
                width={SecuredByShopify.width}
                height={SecuredByShopify.height}
              />
            </ShopifyCallout>
          </ButtonContainer>
        </Form>
      </LoginFormContainer>
      <CanineCareCallout>
        Feel free to reach out with any questions or concerns.
        <br /> Our <SmolLink href={canineCareUrl}>Canine Care team</SmolLink> is
        always happy to help!
      </CanineCareCallout>
      <NewToOllie {...newToOllie} />
    </LoginContainer>
  );
};

DualLoginForm.displayName = 'DualLoginForm';
DualLoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  labelEmail: PropTypes.string.isRequired,
  labelPassword: PropTypes.string.isRequired,
  labelLogin: PropTypes.string.isRequired,
  labelContinue: PropTypes.string.isRequired,
  handleRequestCustomerLogin: PropTypes.func.isRequired,
  forgotPasswordUrl: PropTypes.string.isRequired,
  forgotPasswordLabel: PropTypes.string.isRequired,
  placeholderEmail: PropTypes.string,
  placeholderPassword: PropTypes.string,
  newToOllie: PropTypes.shape({
    subheading: PropTypes.string.isRequired,
    img: PropTypes.object.isRequired,
    heading: PropTypes.string.isRequired,
    getStartedText: PropTypes.string.isRequired,
    getStartedUrl: PropTypes.string.isRequired,
  }),
  customerType: PropTypes.string,
  defaultValues: PropTypes.shape({
    email: PropTypes.string.isRequired,
    password: PropTypes.string,
  }),
  canineCareUrl: PropTypes.string.isRequired,
};
